import React from 'react'
import { Radio, Typography } from 'antd'
import { useIntl } from 'react-intl'

import { getIntegrationPlatformName } from '@vacationtracker/shared/functions/get-integration-platform-name'

import IntlMessages from '../../../../util/IntlMessages'

import { Platform } from '@vacationtracker/shared/types/core-event'

const { Text, Paragraph } = Typography

interface IImportAllToggleProps {
  platform: Platform
  importAllUsers: boolean
  shouldEnableImportAll: boolean
  totalDirectoryUsers: number
  notImportedCount: number
  importUsersAutomatically: boolean
  onToggle: (value: boolean) => void
  onAutoSyncChange: (value: boolean) => void
}

const ImportAllToggle = ({
  platform,
  importAllUsers,
  shouldEnableImportAll,
  totalDirectoryUsers,
  notImportedCount,
  importUsersAutomatically,
  onToggle,
  onAutoSyncChange,
}: IImportAllToggleProps): React.ReactElement => {
  const { formatMessage } = useIntl()
  const platformName = getIntegrationPlatformName(platform)

  return (
    <div className="import-page-info-card">
      <div className="import-page-toggle-row">
        <Text style={{ fontSize: 14 }}>
          {(() => {
            const alreadyImported = totalDirectoryUsers - notImportedCount
            const showRemaining = notImportedCount > 0 && alreadyImported >= 10 && alreadyImported / totalDirectoryUsers >= 0.1
            return showRemaining ? (
              <IntlMessages
                id="components.importUsersForm.importRemainingUsers"
                values={{
                  b: (chunks) => <strong>{chunks}</strong>,
                  remaining: notImportedCount,
                  imported: alreadyImported,
                  total: totalDirectoryUsers,
                  platform: platformName,
                }}
              />
            ) : (
              <IntlMessages
                id="components.importUsersForm.importAllUsers"
                values={{
                  b: (chunks) => <strong>{chunks}</strong>,
                  count: notImportedCount,
                  platform: platformName,
                }}
              />
            )
          })()}
        </Text>
        <Radio.Group optionType="button" value={importAllUsers} onChange={(e) => onToggle(e.target.value as boolean)} style={{ flexShrink: 0 }}>
          <Radio.Button disabled={!shouldEnableImportAll} value={false} data-testid="import-all-no">
            {formatMessage({ id: 'components.importUsersForm.importAllToggleNo' })}
          </Radio.Button>
          <Radio.Button disabled={!shouldEnableImportAll} value={true} data-testid="import-all-yes">
            {formatMessage({ id: 'components.importUsersForm.importAllToggleYes' })}
          </Radio.Button>
        </Radio.Group>
      </div>
      {!shouldEnableImportAll && (
        <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
          <IntlMessages id="components.importUsersForm.importAllUsersExceedingLimt" />
        </Paragraph>
      )}
      {importAllUsers && (
        <div className="import-page-toggle-row" style={{ marginTop: 12 }}>
          <Text style={{ fontSize: 14 }}>
            <IntlMessages id="components.importUsersForm.keepInSync" values={{ platform: platformName }} />
          </Text>
          <Radio.Group optionType="button" value={importUsersAutomatically} onChange={(e) => onAutoSyncChange(e.target.value as boolean)} style={{ flexShrink: 0 }}>
            <Radio.Button value={true} data-testid="auto-sync-yes">
              {formatMessage({ id: 'app.yes' })}
            </Radio.Button>
            <Radio.Button value={false} data-testid="auto-sync-no">
              {formatMessage({ id: 'app.no' })}
            </Radio.Button>
          </Radio.Group>
          {importUsersAutomatically && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              <IntlMessages id="components.importUsersForm.keepInSyncDescription" values={{ platform: platformName }} />
            </Text>
          )}
        </div>
      )}
    </div>
  )
}

export default ImportAllToggle
